import React from 'react'
import ThermometerWithGate from '@/modules/termometro/components/ThermometerWithGate'
import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react'
import type { OpportunityItem } from '../types'
import ObjectiveIcon from './ObjectiveIcon'
import { GlowingEffect } from '@/components/ui/glowing-effect'
import { formatPair } from '../utils/formatPair'
import { Badge } from '@/shared/ui'
import { OpportunityCommunityEntry } from '@/modules/comunidade/components/OpportunityCommunityEntry'
import type { CommunitySummary } from '@/modules/comunidade/services/communityService'

type OpportunityCompactCardProps = {
  item: OpportunityItem
  thermoAllowed: boolean
  noteForMaster?: string
  highlight?: boolean
  interactive?: boolean
  onClick?: () => void
  communityEnabled?: boolean
  communitySummary?: CommunitySummary | null
  showClosedBadge?: boolean
  leadingBadge?: React.ReactNode
}

export function OpportunityCompactCard({
  item,
  thermoAllowed,
  noteForMaster,
  highlight,
  interactive = true,
  onClick,
  communityEnabled,
  communitySummary,
  showClosedBadge = true,
  leadingBadge,
}: OpportunityCompactCardProps) {
  const status = String(item.status || '').toLowerCase()
  const isClosed = status === 'closed'
  const clickable = interactive && typeof onClick === 'function'

  return (
    <div
      id={`opp-card-${item.id}`}
      role={clickable ? 'button' : undefined}
      onClick={clickable ? () => onClick() : undefined}
      className={[
        'relative rounded-xl border bg-[#1c1c1c] p-3 min-h-[110px] md:min-h-[110px]',
        clickable ? 'cursor-pointer active:bg-[#171717]' : 'cursor-default',
        highlight ? 'ring-2 ring-lime-400 border-lime-400' : 'border-[#1c1c1c]',
      ].join(' ')}
    >
      <GlowingEffect
        spread={40}
        glow
        disabled={false}
        proximity={64}
        inactiveZone={0.01}
        borderWidth={3}
      />
      <div className="grid grid-cols-[auto,1fr,auto] grid-rows-[auto,auto,auto] items-center gap-x-3 gap-y-2">
        <div className="row-span-3 col-start-1 flex items-center justify-start">
          <div className="inline-flex items-center rounded p-1">
            {item.logo_url ? (
              <img
                src={item.logo_url}
                alt={item.symbol}
                className="h-[2.65rem] w-[2.65rem] flex-shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="h-[2.65rem] w-[2.65rem] flex-shrink-0 rounded-full bg-muted" />
            )}
          </div>
        </div>

        <div className="col-start-2 col-span-1">
          <div className="min-w-0 flex items-baseline gap-2">
            <span className="truncate text-[13px] font-medium leading-tight">
              {item.name || item.symbol}
            </span>
            <span className="truncate text-[11px] text-foreground/70 leading-tight">
              | {formatPair(item.symbol)}
            </span>
          </div>
        </div>

        <div className="col-start-3 row-start-1 flex items-center justify-end gap-2">
          {showClosedBadge && isClosed ? (
            <Badge variant="soft" color="warning" className="whitespace-nowrap">
              Encerrada
            </Badge>
          ) : null}
          {leadingBadge}
          <span
            className={[
              'whitespace-nowrap text-[12px] font-medium',
              item.objective === 'Long'
                ? 'text-emerald-400'
                : item.objective === 'Short'
                  ? 'text-red-400'
                  : 'text-foreground/80',
            ].join(' ')}
          >
            {item.objective === 'Long'
              ? 'bullish'
              : item.objective === 'Short'
                ? 'bearish'
                : '-'}
          </span>
          <ObjectiveIcon objective={item.objective} />
          {item.nearBreak ? (
            <AlertCircle className="h-4 w-4 md:h-5 md:w-5 text-red-500" />
          ) : item.uiWarning ? (
            <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-amber-400" />
          ) : status === 'active' ? (
            <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-emerald-400" />
          ) : (
            <AlertTriangle className="h-4 w-4 md:h-5 md:w-5 text-amber-400" />
          )}
        </div>

        <div className="col-start-2 col-span-2 mt-1">
          <div className="relative">
            <ThermometerWithGate
              enabled={thermoAllowed}
              value={item.thermometerPercent ?? item.confluencePercent}
              className="h-2.5 w-full"
              note={noteForMaster}
              noteClassName="text-[10px] md:text-xs"
            />
            {thermoAllowed && (
              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-[11px] font-medium text-foreground/80">
                {(item.thermometerPercent ?? item.confluencePercent)}%
              </span>
            )}
          </div>
        </div>

        {communityEnabled && (
          <div className="col-start-2 col-span-2 mt-1">
            <OpportunityCommunityEntry
              opportunityId={Number(item.id)}
              symbol={item.symbol}
              name={item.name}
              logoUrl={item.logo_url}
              enabled={!!communityEnabled}
              summary={communitySummary ?? null}
            />
          </div>
        )}

        <div className="col-start-2 col-span-2">
          <div className="min-w-0 flex items-center justify-between">
            <div className="min-w-0 whitespace-nowrap text-[12px] text-foreground/80">
              {[item.fractalLower, item.fractalUpper].filter(Boolean).join(' / ') || '-'}
            </div>
            <div className="min-w-0 whitespace-nowrap text-[12px] text-foreground/80 text-right">
              {item.recommendation || '-'}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
